<template>
  <div class="container-fluid">
    <admin-dashboard-widgets/>
    <div class="row dashboard-block">
      <div class="col-xs-12" v-if="$vlastMode && $showDepartmentField">
        <scatterplot
          class="chart-card"
          uniqueName="total_hours_per_player"
          :showBrush="true"
          :name="'hours_completed_per_' + $starLabel">
        </scatterplot>
      </div>
      <div class="col-xs-12" v-if="$vlastMode && $showDepartmentField">
        <bar-chart
          class="chart-card"
          uniqueName="booking_request_by_opportunity_type"
          :showBrush="false"
          name="booking_requests_by_summary">
        </bar-chart>
      </div>
      <div class="col-xs-12" v-if="$vlastMode && $showDepartmentField">
        <bar-chart
          class="chart-card"
          uniqueName="total_hours_per_department"
          :showBrush="false"
          name="hours_per_department">
        </bar-chart>
      </div>
      <div class="col-xs-12">
        <datetime-line-chart
          v-for="chartName in this.getDateTimeChartsToShow()"
          :key="chartName"
          class="chart-card"
          :uniqueName="chartName"
          type="single"
          :showBrush="false"
          :channelData="false"
          :name="chartName">
        </datetime-line-chart>
      </div>
    </div>

    <div v-if="$chatsModuleEnabled" class="dashboard-block">
      <h2 class="has-button">
        Active Chats
        &ndash;
        {{ lists.active_threads_count }}
      </h2>

      <div class="text-center alert alert-default" v-if="haveNoActiveThreads">
        There are no active chats
      </div>
      <ul v-else class="list-items">
        <thread-list-item
          v-for="thread in activeThreads"
          :key="thread.id"
          :thread="thread"
          :booking="thread.booking"/>
      </ul>
      <div class="text-right">
        <router-link :to="{ name: 'threads.index' }" class="btn btn-info" style="letter-spacing: 2px">View All</router-link>
      </div>
    </div>

    <div class="dashboard-block">
      <h2 class="has-button">
        {{ $t('Requests') }} Ending Soon
        &ndash;
        {{ lists.expiring_requests_count }}
      </h2>

      <div class="text-center alert alert-default" v-if="haveNoExpiringRequests">
        There are no {{ $t('requests') }} ending soon.
      </div>
      <ul v-else class="list-items">
        <admin-dashboard-expiring-requests-list-item
          v-for="booking in expiringRequests"
          :key="booking.id"
          :booking="booking"/>
      </ul>
      <div class="text-right">
        <router-link :to="{ name: 'requests.index' }" class="btn btn-info" style="letter-spacing: 2px">View All</router-link>
      </div>
    </div>

    <div class="dashboard-block">
      <h2 class="has-button">
        Pending {{ $t('Requests') }}
        &ndash;
        {{ lists.vetting_requests_count }}
      </h2>

      <div class="text-center alert alert-default" v-if="haveNoVettingRequests">
        There are no vetting {{ $t('requests') }}.
      </div>
      <ul v-else class="list-items">
        <admin-dashboard-vetting-requests-list-item
          v-for="booking in vettingRequests"
          :key="booking.id"
          :booking="booking"/>
      </ul>
      <div class="text-right">
        <h2>
          <router-link :to="{ name: 'vetting.index' }" class="btn btn-info" style="letter-spacing: 2px">View All</router-link>
        </h2>
      </div>
    </div>

    <div class="dashboard-block">
      <h2 class="has-button">
        Bookings Not Marked Complete
        &ndash;
        {{ lists.bookings_not_completed_count }}
      </h2>

      <div class="text-center alert alert-default" v-if="haveNoBookingsNotCompleted">
        There are no bookings not marked as complete.
      </div>
      <ul v-else class="list-items">
        <admin-dashboard-bookings-not-completed-list-item
          v-for="booking in bookingsNotCompleted"
          :key="booking.id"
          :booking="booking"/>
      </ul>
      <div class="text-right">
        <router-link :to="{ name: 'bookings.index' }" class="btn btn-info" style="letter-spacing: 2px">View All</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import DatetimeLineChart from '../bi/DatetimeLineChart'
import BarChart from '../bi/BarChart'
import Scatterplot from '../bi/Scatterplot'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'
import { mapState } from 'vuex'
import AdminDashboardWidgets from './AdminDashboardWidgets'
import AdminDashboardVettingRequestsListItem from './AdminDashboardVettingRequestsListItem'
import AdminDashboardExpiringRequestsListItem from './AdminDashboardExpiringRequestsListItem'
import AdminDashboardBookingsNotCompletedListItem from './AdminDashboardBookingsNotCompletedListItem'
import ThreadListItem from '../message/ThreadListItem'
import { CommonTracker } from 'mixins/analytics'

Vue.component('apexchart', VueApexCharts)

export default {
  components: {
    BarChart,
    Scatterplot,
    DatetimeLineChart,
    AdminDashboardWidgets,
    AdminDashboardVettingRequestsListItem,
    AdminDashboardExpiringRequestsListItem,
    AdminDashboardBookingsNotCompletedListItem,
    ThreadListItem
  },

  mixins: [CommonTracker],

  methods: {
    getDateTimeChartsToShow () {
      const charts = []

      if (!this.$showDepartmentField) {
        charts.push('completed')
        charts.push('requests')
        charts.push('bookings')
      }

      if (!this.$vlastMode) {
        charts.push('leads')
      }

      return charts
    }
  },

  mounted () {
    this.trackEvent(this.analytics.V2.NavigationDashboardOpenEvent)
  },

  data () {
    return {
      chartOptions: {
        chart: {
          type: 'line',
          zoom: {
            type: 'xy'
          }
        },
        xaxis: {
          type: 'datetime'
        }
      }
    }
  },

  computed: {
    ...mapState('dashboard', ['lists']),

    activeThreads () {
      return this.lists.active_threads || []
    },

    vettingRequests () {
      return this.lists.vetting_requests || []
    },

    expiringRequests () {
      return this.lists.expiring_requests || []
    },

    bookingsNotCompleted () {
      return this.lists.bookings_not_completed || []
    },

    haveNoActiveThreads () {
      return this.lists.active_threads === undefined ||
        this.lists.active_threads.length === 0
    },

    haveNoVettingRequests () {
      return this.lists.vetting_requests === undefined ||
        this.lists.vetting_requests.length === 0
    },

    haveNoExpiringRequests () {
      return this.lists.expiring_requests === undefined ||
        this.lists.expiring_requests.length === 0
    },

    haveNoBookingsNotCompleted () {
      return this.lists.bookings_not_completed === undefined ||
        this.lists.bookings_not_completed.length === 0
    }
  }
}
</script>
